import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import clsx from 'clsx'
import type { Map as MapboxMapInstance, SourceSpecification } from 'mapbox-gl'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { FullscreenControl } from 'react-map-gl/mapbox'
import styled, { createGlobalStyle } from 'styled-components'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { MapboxMap } from 'components/Common/Mapbox/MapboxMap'
import type { MapboxMapMoveEvent, NavigationControlPosition } from 'components/Common/Mapbox/MapboxMap'
import { MapboxMarker } from 'components/Common/Mapbox/MapboxMarker'
import { MapboxPopup } from 'components/Common/Mapbox/MapboxPopup'
import {
  isMapClusterFeature,
  useMapboxClusters,
} from 'components/Common/Mapbox/useMapboxClusters'
import type { MapClusterFeature, MapPointFeature } from 'components/Common/Mapbox/useMapboxClusters'
import { NEARBY_MAP_MARKER_TYPE, PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import { MapboxProvider } from 'contexts/MapboxContext'
import { getWidth } from 'lib/window/getViewport'
import { getLongitudeSpan } from 'lib/maps/bounds'
import { fitBoundsWithExtraSpace, getBoundsFromMarkers, panMapWithPixelOffset } from 'lib/maps/mapboxMapUtils'
import type { LngLat, MapHandle } from 'lib/maps/types'

import { styles as clusterStyles } from 'components/Common/PropertyMap/Cluster'
import { getMarkerImage } from './markerPins'
import type { PropertyMapOptions } from './types'

// over 480 - the desktop version of the fitted map looks good, less than 480 - a lot of grey space on top and under the map
const MAX_MOBILE_WIDTH = 480
// Intentionally kept at 14 for PropertyMap single-marker parity; 15 renders too far out in this UI.
const SINGLE_MARKER_ZOOM = 14
const PROPERTY_MAP_MULTI_MARKER_BOUNDS_EXPANSION = 0.12
const PROPERTY_MAP_WRAPPED_BOUNDS_EXPANSION = 0.02
const PROPERTY_MAP_TWO_MARKER_MIN_ZOOM = 6.2
const PROPERTY_MAP_TWO_MARKER_MAX_LATITUDE_SPAN = 8
const PROPERTY_MAP_TWO_MARKER_MAX_LONGITUDE_SPAN = 8
const ROADMAP_STYLE = 'mapbox://styles/mapbox/streets-v12'
const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-streets-v12'
const DEFAULT_MARKER_OPTIONS = {
  xOffset: 0,
  yOffset: 0,
}
const TERRAIN_DEM_SOURCE_ID = 'property-map-terrain-dem'
const TERRAIN_CONTOUR_SOURCE_ID = 'property-map-terrain'
const TERRAIN_HILLSHADE_LAYER_ID = 'property-map-hillshade'
const TERRAIN_CONTOUR_INDEX_LAYER_ID = 'property-map-contours-index'
const TERRAIN_CONTOUR_DETAIL_LAYER_ID = 'property-map-contours-detail'
const TILTED_MAP_PITCH = 45
const PITCH_SYNC_EPSILON = 0.1
const DEFAULT_NAVIGATION_CONTROL_POSITION = 'bottom-right'
const GOOGLE_CONTROL_POSITION = {
  BLOCK_END_INLINE_START: 2,
  BLOCK_START_INLINE_CENTER: 3,
  BLOCK_START_INLINE_END: 4,
  BLOCK_START_INLINE_START: 5,
  BOTTOM_LEFT: 7,
  INLINE_END_BLOCK_START: 11,
  INLINE_START_BLOCK_END: 13,
  INLINE_START_BLOCK_START: 14,
  LEFT_TOP: 17,
  TOP_CENTER: 21,
  TOP_LEFT: 22,
  TOP_RIGHT: 23,
} as const
const TOP_RIGHT_ZOOM_CONTROL_POSITIONS = new Set<number>([
  GOOGLE_CONTROL_POSITION.BLOCK_START_INLINE_CENTER,
  GOOGLE_CONTROL_POSITION.BLOCK_START_INLINE_END,
  GOOGLE_CONTROL_POSITION.INLINE_END_BLOCK_START,
  GOOGLE_CONTROL_POSITION.TOP_CENTER,
  GOOGLE_CONTROL_POSITION.TOP_RIGHT,
])
const TOP_LEFT_ZOOM_CONTROL_POSITIONS = new Set<number>([
  GOOGLE_CONTROL_POSITION.BLOCK_START_INLINE_START,
  GOOGLE_CONTROL_POSITION.INLINE_START_BLOCK_START,
  GOOGLE_CONTROL_POSITION.LEFT_TOP,
  GOOGLE_CONTROL_POSITION.TOP_LEFT,
])
const BOTTOM_LEFT_ZOOM_CONTROL_POSITIONS = new Set<number>([
  GOOGLE_CONTROL_POSITION.BLOCK_END_INLINE_START,
  GOOGLE_CONTROL_POSITION.BOTTOM_LEFT,
  GOOGLE_CONTROL_POSITION.INLINE_START_BLOCK_END,
])

interface Props {
  className?: string
  height?: string | number
  markerOptions?: {
    xOffset: number
    yOffset: number
  }
  markers: Array<App.MapMarker>
  multipleMarkers?: boolean
  onSelectMarker?: (marker: App.MapMarker) => void
  renderInfoWindow?: (item: App.MapMarker, onCloseClick: () => void) => React.ReactNode
  onDrag?: () => void
  mapOptions?: PropertyMapOptions
  wrapperStyle?: React.CSSProperties
  // Keep the map centered on the first marker rather than fitting all markers.
  focusOnFirstMarker?: boolean
  highlightedMarkerId?: string
  disablePanOnMarkerSelect?: boolean
  enableNearbyMarkerFullscreen?: boolean
  showActiveMarkerState?: boolean
}

export interface PropertyMapHandle {
  focusMarker: (markerId: string) => void
}

interface ClusterableMarker extends App.MapMarker, LngLat {}

interface PropertyMapMarkerProps {
  marker: App.MapMarker
  active: boolean
  onSelect: (marker: App.MapMarker) => void
}

interface PropertyMapClusterMarkerProps {
  theme: 'white' | 'black'
  count: number | string
  lat: number
  lng: number
  clusterId: number
  onSelect: (clusterId: number, coordinate: LngLat) => void
}

type PropertyMapClusterFeature = MapClusterFeature | MapPointFeature<ClusterableMarker>
type SecondaryControlView = 'map' | 'satellite' | undefined

interface FitMarkersGuardState {
  mapHandle: MapHandle | null
  mapboxMap: MapboxMapInstance | null
  markersFitKey: string
  focusOnFirstMarker?: boolean
}

interface FullscreenCapableElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void
}

interface FullscreenCapableDocument extends Document {
  webkitFullscreenElement?: Element | null
}

const MapPopupStyles = createGlobalStyle`
  .PropertyMap .mapboxgl-map {
    overflow: hidden;
    isolation: isolate;
    z-index: 0;
  }

  .PropertyMap [mapboxgl-children] {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    overflow: hidden;
    z-index: 2;
    pointer-events: none;
  }

  .PropertyMap [mapboxgl-children] > * {
    pointer-events: auto;
  }

  .PropertyMap .mapboxgl-popup {
    z-index: 1;
  }

  .property-map-popup .mapboxgl-popup-content {
    padding: 0;
    overflow: hidden;
  }

  .property-map-popup .mapboxgl-popup-close-button {
    display: none;
  }

  .PropertyMap .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group button {
    width: ${rem(40)};
    height: ${rem(40)};
  }

  .PropertyMap .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group,
  .PropertyMap .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group button:only-child {
    border-radius: ${rem(1)};
  }

  .PropertyMap .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group button.mapboxgl-ctrl-fullscreen .mapboxgl-ctrl-icon,
  .PropertyMap .mapboxgl-ctrl-top-right .mapboxgl-ctrl-group button.mapboxgl-ctrl-shrink .mapboxgl-ctrl-icon {
    width: ${rem(18)};
    height: ${rem(18)};
    margin: auto;
    background-size: ${rem(18)} ${rem(18)};
  }

  .PropertyMap .mapboxgl-ctrl button.mapboxgl-ctrl-fullscreen .mapboxgl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%23333' stroke-linecap='square' stroke-linejoin='miter' stroke-width='2.4' viewBox='0 0 20 20'%3E%3Cpath d='M7 2.5H2.5V7M13 2.5h4.5V7M17.5 13v4.5H13M7 17.5H2.5V13'/%3E%3C/svg%3E");
  }

  .PropertyMap .mapboxgl-ctrl button.mapboxgl-ctrl-shrink .mapboxgl-ctrl-icon {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' stroke='%23333' stroke-linecap='square' stroke-linejoin='miter' stroke-width='2.4' viewBox='0 0 20 20'%3E%3Cpath d='M8 7H4.5V3.5M12 7h3.5V3.5M8 13H4.5v3.5M12 13h3.5v3.5'/%3E%3C/svg%3E");
  }
`

const MarkerButton = styled.button`
  display: block;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`

const MarkerImage = styled.img`
  display: block;
  max-width: none;
`

const ClusterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${`${clusterStyles.white.width}px`};
  height: ${`${clusterStyles.white.height}px`};
  padding: 0;
  border: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  cursor: pointer;
  font-weight: 700;

  &.white {
    background-image: url(${clusterStyles.white.url});
    color: ${clusterStyles.white.textColor};
  }

  &.black {
    background-image: url(${clusterStyles.black.url});
    color: ${clusterStyles.black.textColor};
  }
`

const MapRoot = styled.div`
  position: relative;
  overflow: hidden;
  isolation: isolate;
  z-index: 0;
`

const TopLeftControls = styled.div`
  display: flex;
  align-items: center;
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(2)};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const MapTypeControlsContainer = styled.div`
  position: absolute;
  top: ${rem(10)};
  left: ${rem(10)};
  z-index: 2;
  display: flex;
  align-items: flex-start;
`

const RightControlsContainer = styled.div`
  position: absolute;
  top: ${rem(10)};
  right: ${rem(10)};
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 0;

  &.withFullscreenControl {
    top: ${rem(50)};
  }
`

const TiltButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  padding: 0;
  border: 0;
  border-radius: ${rem(1)};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.flat.small};
  cursor: pointer;

  &.isActive {
    background: ${props => props.theme.palette.neutral.default.seven};
  }
`

const TiltButtonIcon = styled.span`
  display: block;
  width: ${rem(18)};
  height: ${rem(16)};
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 16'%3E%3Cpath fill='%23333' d='M0 16h8V9H0zm10 0h8V9h-8zM0 7h8V0H0zm10-7v7h8V0z'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: ${rem(18)} ${rem(16)};

  &.isTilted {
    height: ${rem(13)};
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 13'%3E%3Cpath fill='%23333' d='M2.75 5H10V0H4.4zM0 13h10V7H2zm20-6h-8v6h10zm-2.4-7H12v5h7.25z'/%3E%3C/svg%3E");
    background-size: ${rem(18)} ${rem(13)};
  }
`

const RotateButtonIcon = styled.span`
  display: block;
  width: ${rem(20)};
  height: ${rem(20)};
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='none' d='M0 0h24v24H0z'/%3E%3Cpath fill='%23333' d='m12.06 9.06 4-4-4-4-1.41 1.41 1.59 1.59h-.18c-2.3 0-4.6.88-6.35 2.64-3.52 3.51-3.52 9.21 0 12.72 1.5 1.5 3.4 2.36 5.36 2.58v-2.02c-1.44-.21-2.84-.86-3.95-1.97-2.73-2.73-2.73-7.17 0-9.9 1.37-1.37 3.16-2.05 4.95-2.05h.17l-1.59 1.59zm8.94 3c-.19-1.74-.88-3.32-1.91-4.61l-1.43 1.43c.69.92 1.15 2 1.32 3.18zm-7.94 7.92V22c1.74-.19 3.32-.88 4.61-1.91l-1.43-1.43c-.91.68-2 1.15-3.18 1.32m4.6-2.74 1.43 1.43c1.04-1.29 1.72-2.88 1.91-4.61h-2.02c-.17 1.18-.64 2.27-1.32 3.18'/%3E%3C/svg%3E");
  background-position: center;
  background-repeat: no-repeat;
  background-size: ${rem(20)} ${rem(20)};
`

const PrimaryControlButtonWrapper = styled.div`
  position: relative;

  &:not(:last-child) > button {
    border-right: 1px solid ${props => props.theme.palette.neutral.default.six};
  }
`

const SecondaryTopLeftControls = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(2)};
  box-shadow: ${props => props.theme.shadow.flat.small};

  &.alignRight {
    left: auto;
    right: 0;
  }
`

const MapTypeButton = styled.button`
  height: ${rem(40)};
  padding: ${rem(8)} ${rem(14)};
  border: 0;
  background: transparent;
  color: ${props => props.theme.palette.neutral.default.two};
  cursor: pointer;
  font-size: ${rem(18)};
  font-weight: 400;
  line-height: ${rem(22)};

  &.isActive {
    background: ${props => props.theme.palette.neutral.default.eight};
    font-weight: 700;
  }
`

const OptionCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  min-height: ${rem(38)};
  padding: ${rem(8)} ${rem(10)};
  cursor: pointer;
  color: ${props => props.theme.palette.neutral.default.two};
  font-size: ${rem(17)};
  font-weight: 400;
  line-height: ${rem(20)};
  user-select: none;
  white-space: nowrap;
`

const OptionCheckbox = styled.input`
  width: ${rem(14)};
  height: ${rem(14)};
  margin: 0;
  accent-color: ${props => props.theme.palette.neutral.default.one};
`

function getMarkerDimensions(markerType: App.MapMarker['type']) {
  switch (markerType) {
    case PROPERTY_MAP_MARKER_TYPE:
      return { width: 53, height: 58 }
    case NEARBY_MAP_MARKER_TYPE:
      return { width: 45, height: 49 }
    case 'cruise-pill':
      return { width: 70, height: 30 }
    case 'cruise-pill-icon':
      return { width: 120, height: 30 }
    default:
      return { width: 48, height: 53 }
  }
}

function getNavigationControlPosition(mapOptions?: PropertyMapOptions): NavigationControlPosition {
  const zoomControlOptions = mapOptions?.zoomControlOptions
  const position = typeof zoomControlOptions === 'object' && zoomControlOptions !== null
    ? (zoomControlOptions as { position?: unknown }).position
    : undefined

  if (typeof position !== 'number') {
    return DEFAULT_NAVIGATION_CONTROL_POSITION
  }

  // Mapbox only supports corner positions, so we map Google/legacy top-row positions
  // to the closest supported corner rather than silently dropping the caller config.
  if (TOP_RIGHT_ZOOM_CONTROL_POSITIONS.has(position)) {
    return 'top-right'
  }

  if (TOP_LEFT_ZOOM_CONTROL_POSITIONS.has(position)) {
    return 'top-left'
  }

  if (BOTTOM_LEFT_ZOOM_CONTROL_POSITIONS.has(position)) {
    return 'bottom-left'
  }

  return 'bottom-right'
}

function getPropertyMapConfig(mapOptions?: PropertyMapOptions) {
  const gestureHandling = mapOptions?.gestureHandling
  const interactionEnabled = gestureHandling !== 'none'

  return {
    minZoom: typeof mapOptions?.minZoom === 'number' ? mapOptions.minZoom : undefined,
    maxZoom: typeof mapOptions?.maxZoom === 'number' ? mapOptions.maxZoom : undefined,
    showNavigationControl: mapOptions?.zoomControl !== false,
    navigationControlPosition: getNavigationControlPosition(mapOptions),
    showFullscreenControl: mapOptions?.fullscreenControl !== false,
    showMapTypeControl: mapOptions?.mapTypeControl !== false,
    interactionEnabled,
    dragRotate: false,
    dragPan: interactionEnabled,
    doubleClickZoom: mapOptions?.disableDoubleClickZoom === true ? false : interactionEnabled,
    touchZoomRotate: interactionEnabled,
    touchPitch: false,
    scrollZoom: mapOptions?.scrollwheel === false ? false : interactionEnabled,
  }
}

function fitMarkersToMap(map: MapHandle | null, markers: Array<App.MapMarker>, focusOnFirstMarker?: boolean) {
  if (!map || markers.length === 0) {
    return
  }

  if (focusOnFirstMarker || markers.length === 1) {
    map.setViewport({
      longitude: markers[0].longitude,
      latitude: markers[0].latitude,
      zoom: SINGLE_MARKER_ZOOM,
    })
    return
  }

  const bounds = getBoundsFromMarkers(markers)
  if (!bounds) {
    return
  }

  if (getWidth() <= MAX_MOBILE_WIDTH && getLongitudeSpan(bounds.east, bounds.west) > 100) {
    map.setViewport({
      longitude: markers[0]?.longitude ?? 0,
      latitude: markers[0]?.latitude ?? 0,
      zoom: 1,
    })
    return
  }

  fitBoundsWithExtraSpace(map, bounds, {
    expansion: PROPERTY_MAP_MULTI_MARKER_BOUNDS_EXPANSION,
    wrappedExpansion: PROPERTY_MAP_WRAPPED_BOUNDS_EXPANSION,
  })
}

function normaliseLongitude(longitude: number) {
  if (longitude > 180) {
    return longitude - 360
  }

  if (longitude < -180) {
    return longitude + 360
  }

  return longitude
}

function getMapBoundsCenter(bounds: {
  west: number
  south: number
  east: number
  north: number
}) {
  const longitudeSpan = getLongitudeSpan(bounds.east, bounds.west)

  return {
    lat: (bounds.south + bounds.north) / 2,
    lng: normaliseLongitude(bounds.west + longitudeSpan / 2),
  }
}

function requestMapFullscreenIfNeeded(mapHandle: MapHandle | null) {
  const fullscreenDocument = document as FullscreenCapableDocument
  const mapContainer = mapHandle?.getContainer() as FullscreenCapableElement | null
  const fullscreenElement = fullscreenDocument.fullscreenElement ?? fullscreenDocument.webkitFullscreenElement
  const requestFullscreen = mapContainer?.requestFullscreen ?? mapContainer?.webkitRequestFullscreen

  if (!mapContainer || fullscreenElement != null) {
    return
  }

  if (!requestFullscreen) {
    return
  }

  try {
    const result = requestFullscreen.call(mapContainer)
    Promise.resolve(result).catch(() => undefined)
  } catch {
    return
  }
}

function getStyleLayers(map: MapboxMapInstance) {
  return map.getStyle()?.layers ?? []
}

function getLabelLayerIds(map: MapboxMapInstance) {
  return getStyleLayers(map)
    .filter((layer) => layer.type === 'symbol' && map.getLayoutProperty(layer.id, 'text-field') != null)
    .map((layer) => layer.id)
}

function getFirstSymbolLayerId(map: MapboxMapInstance) {
  return getStyleLayers(map).find((layer) => layer.type === 'symbol')?.id
}

function setLayerVisibility(map: MapboxMapInstance, layerIds: Array<string>, isVisible: boolean) {
  const visibility = isVisible ? 'visible' : 'none'

  layerIds.forEach((layerId) => {
    if (!map.getLayer(layerId)) {
      return
    }

    map.setLayoutProperty(layerId, 'visibility', visibility)
  })
}

function ensureTerrainSource(map: MapboxMapInstance) {
  if (!map.getSource(TERRAIN_DEM_SOURCE_ID)) {
    map.addSource(TERRAIN_DEM_SOURCE_ID, {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    } satisfies SourceSpecification)
  }
}

function ensureTerrainContourSource(map: MapboxMapInstance) {
  if (!map.getSource(TERRAIN_CONTOUR_SOURCE_ID)) {
    map.addSource(TERRAIN_CONTOUR_SOURCE_ID, {
      type: 'vector',
      url: 'mapbox://mapbox.mapbox-terrain-v2',
    } satisfies SourceSpecification)
  }
}

function ensureTerrainLayers(map: MapboxMapInstance) {
  const beforeLayerId = getFirstSymbolLayerId(map)

  ensureTerrainSource(map)
  ensureTerrainContourSource(map)

  if (!map.getLayer(TERRAIN_HILLSHADE_LAYER_ID)) {
    map.addLayer({
      id: TERRAIN_HILLSHADE_LAYER_ID,
      type: 'hillshade',
      source: TERRAIN_DEM_SOURCE_ID,
      paint: {
        'hillshade-shadow-color': '#5f6a57',
        'hillshade-highlight-color': '#c8d0bf',
        'hillshade-accent-color': '#89957e',
        'hillshade-exaggeration': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9, 0.55,
          12, 0.45,
          14, 0.22,
          15, 0.06,
          16, 0,
        ],
      },
    }, beforeLayerId)
  }

  if (!map.getLayer(TERRAIN_CONTOUR_INDEX_LAYER_ID)) {
    map.addLayer({
      id: TERRAIN_CONTOUR_INDEX_LAYER_ID,
      type: 'line',
      source: TERRAIN_CONTOUR_SOURCE_ID,
      'source-layer': 'contour',
      minzoom: 9,
      maxzoom: 12,
      filter: [
        'any',
        ['==', ['get', 'index'], -1],
        ['==', ['get', 'index'], 5],
        ['==', ['get', 'index'], 10],
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#776756',
        'line-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9, 0.18,
          10, 0.22,
          11.9, 0.28,
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          9, 0.7,
          10, 0.9,
          11.9, 1.1,
        ],
      },
    }, beforeLayerId)
  }

  if (!map.getLayer(TERRAIN_CONTOUR_DETAIL_LAYER_ID)) {
    map.addLayer({
      id: TERRAIN_CONTOUR_DETAIL_LAYER_ID,
      type: 'line',
      source: TERRAIN_CONTOUR_SOURCE_ID,
      'source-layer': 'contour',
      minzoom: 12,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': '#776756',
        'line-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12, 0.18,
          13, 0.24,
          15, 0.32,
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          12, 0.6,
          13, 0.8,
          15, 1.2,
        ],
      },
    }, beforeLayerId)
  }
}

function applyTerrainVisibility(map: MapboxMapInstance, isVisible: boolean) {
  if (!map.isStyleLoaded()) {
    return
  }

  ensureTerrainLayers(map)
  setLayerVisibility(map, [
    TERRAIN_HILLSHADE_LAYER_ID,
    TERRAIN_CONTOUR_INDEX_LAYER_ID,
    TERRAIN_CONTOUR_DETAIL_LAYER_ID,
  ], isVisible)
}

function applySatelliteLabelsVisibility(map: MapboxMapInstance, isVisible: boolean) {
  if (!map.isStyleLoaded()) {
    return
  }

  setLayerVisibility(map, getLabelLayerIds(map), isVisible)
}

const PropertyMapMarker = React.memo(function PropertyMapMarker({
  marker,
  active,
  onSelect,
}: PropertyMapMarkerProps) {
  const { width, height } = getMarkerDimensions(marker.type)
  const coordinate = useMemo(() => ({ lat: marker.latitude, lng: marker.longitude }), [marker.latitude, marker.longitude])
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onSelect(marker)
  }, [marker, onSelect])

  return (
    <MapboxMarker
      anchor="top-left"
      coordinate={coordinate}
      offset={[-Math.round(width / 2), -height]}
    >
      <MarkerButton
        type="button"
        aria-label={marker.name ?? marker.id}
        onClick={handleClick}
      >
        <MarkerImage
          src={getMarkerImage(marker, active)}
          alt=""
          width={width}
          height={height}
        />
      </MarkerButton>
    </MapboxMarker>
  )
})

const PropertyMapClusterMarker = React.memo(function PropertyMapClusterMarker({
  theme,
  count,
  lat,
  lng,
  clusterId,
  onSelect,
}: PropertyMapClusterMarkerProps) {
  const coordinate = useMemo(() => ({ lat, lng }), [lat, lng])
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onSelect(clusterId, coordinate)
  }, [clusterId, coordinate, onSelect])

  return (
    <MapboxMarker
      anchor="center"
      coordinate={coordinate}
    >
      <ClusterButton
        className={clsx(theme)}
        type="button"
        onClick={handleClick}
      >
        {count}
      </ClusterButton>
    </MapboxMarker>
  )
})

const PropertyMap = forwardRef<PropertyMapHandle, React.PropsWithChildren<Props>>(function PropertyMap(
  {
    markers,
    multipleMarkers,
    className,
    height,
    mapOptions,
    markerOptions = DEFAULT_MARKER_OPTIONS,
    onDrag,
    onSelectMarker,
    renderInfoWindow,
    wrapperStyle,
    focusOnFirstMarker,
    highlightedMarkerId,
    disablePanOnMarkerSelect,
    enableNearbyMarkerFullscreen = true,
    showActiveMarkerState = true,
  },
  ref,
) {
  const intl = useIntl()
  const [mapHandle, setMapHandle] = useState<MapHandle | null>(null)
  const [mapboxMap, setMapboxMap] = useState<MapboxMapInstance | null>(null)
  const [zoom, setZoom] = useState(17)
  const [isSatelliteView, setIsSatelliteView] = useState(false)
  const [isTilted, setIsTilted] = useState(false)
  const [showTerrain, setShowTerrain] = useState(false)
  const [showSatelliteLabels, setShowSatelliteLabels] = useState(true)
  const [secondaryControlView, setSecondaryControlView] = useState<SecondaryControlView>(undefined)
  const [activeMarker, setActiveMarker] = useState<App.MapMarker | undefined>(undefined)
  const [activeMarkerInfoWindowOpen, setActiveMarkerInfoWindowOpen] = useState(false)

  const {
    showFullscreenControl,
    showMapTypeControl,
    interactionEnabled,
    ...mapboxMapProps
  } = useMemo(() => getPropertyMapConfig(mapOptions), [mapOptions])
  const mapStyle = useMemo(() => isSatelliteView ? SATELLITE_STYLE : ROADMAP_STYLE, [isSatelliteView])
  const markersKey = useMemo(() => markers.map(marker => marker.id).join(','), [markers])
  const markersFitKey = useMemo(
    () => markers.map(marker => `${marker.id}:${marker.latitude}:${marker.longitude}`).join('|'),
    [markers],
  )
  const fitMarkersGuardRef = useRef<FitMarkersGuardState | undefined>(undefined)
  const initialViewport = useMemo(() => ({
    latitude: markers[0]?.latitude ?? 0,
    longitude: markers[0]?.longitude ?? 0,
    zoom: (focusOnFirstMarker || markers.length === 1) ? SINGLE_MARKER_ZOOM : 3,
  }), [focusOnFirstMarker, markers])

  const groupedMarkers = useMemo(() => markers.reduce<{
    white: Array<App.MapMarker>
    black: Array<App.MapMarker>
  }>((acc, marker) => {
    if (marker.type === PROPERTY_MAP_MARKER_TYPE) {
      acc.white.push(marker)
    } else {
      acc.black.push(marker)
    }

    return acc
  }, {
    white: [],
    black: [],
  }), [markers])

  const whiteClusterMarkers = useMemo<Array<ClusterableMarker>>(() => groupedMarkers.white.map(marker => ({
    ...marker,
    lat: marker.latitude,
    lng: marker.longitude,
  })), [groupedMarkers.white])

  const blackClusterMarkers = useMemo<Array<ClusterableMarker>>(() => groupedMarkers.black.map(marker => ({
    ...marker,
    lat: marker.latitude,
    lng: marker.longitude,
  })), [groupedMarkers.black])

  const { clusters: whiteClusters, handleClusterClick: handleWhiteClusterClick } = useMapboxClusters({
    markers: whiteClusterMarkers,
    mapHandle,
    zoom,
    disableClustering: !multipleMarkers,
  })

  const { clusters: blackClusters, handleClusterClick: handleBlackClusterClick } = useMapboxClusters({
    markers: blackClusterMarkers,
    mapHandle,
    zoom,
    disableClustering: !multipleMarkers,
  })

  const toggleActiveMarkerInfoWindow = useCallback(() => {
    setActiveMarkerInfoWindowOpen((previousValue) => !previousValue)
  }, [])

  const focusMarker = useCallback((markerId: string) => {
    const marker = markers.find((nextMarker) => nextMarker.id === markerId)

    if (!marker) {
      return
    }

    setActiveMarker(marker)
    setActiveMarkerInfoWindowOpen(true)
    onSelectMarker?.(marker)

    if (!disablePanOnMarkerSelect) {
      panMapWithPixelOffset(
        mapHandle,
        { lat: marker.latitude, lng: marker.longitude },
        markerOptions?.xOffset,
        markerOptions?.yOffset,
      )
    }
  }, [disablePanOnMarkerSelect, mapHandle, markerOptions?.xOffset, markerOptions?.yOffset, markers, onSelectMarker])

  useImperativeHandle(ref, () => ({
    focusMarker,
  }), [focusMarker])

  const handleMarkerSelect = useCallback((marker: App.MapMarker) => {
    if (!multipleMarkers && marker.openGoogleMapsOnSelect !== false) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${marker.latitude},${marker.longitude}`)
      return
    }

    if (showFullscreenControl && enableNearbyMarkerFullscreen && marker.openGoogleMapsOnSelect === false) {
      requestMapFullscreenIfNeeded(mapHandle)
    }

    focusMarker(marker.id)
  }, [enableNearbyMarkerFullscreen, focusMarker, mapHandle, multipleMarkers, showFullscreenControl])

  const handleMapLoad = useCallback((nextMapHandle: MapHandle) => {
    setMapHandle(nextMapHandle)
    setZoom(nextMapHandle.getZoom())
  }, [])
  const handleMapReady = useCallback((nextMap: MapboxMapInstance) => {
    setMapboxMap(nextMap)
  }, [])

  useEffect(() => {
    if (!mapboxMap) {
      return
    }

    mapboxMap.dragRotate.disable()
    mapboxMap.touchPitch.disable()

    if (interactionEnabled) {
      mapboxMap.touchZoomRotate.enable()
      mapboxMap.touchZoomRotate.disableRotation()
    }
  }, [interactionEnabled, mapboxMap])

  const handleMapMove = useCallback((event: MapboxMapMoveEvent) => {
    setZoom(event.viewport.zoom)
  }, [])

  const handleMapMoveEnd = useCallback(() => {
    onDrag?.()
  }, [onDrag])

  const handleMapClick = useCallback(() => {
    setActiveMarkerInfoWindowOpen(false)
  }, [])
  const handleShowRoadmap = useCallback(() => {
    if (mapboxMap && (isTilted || mapboxMap.getBearing() !== 0)) {
      mapboxMap.easeTo({
        pitch: 0,
        bearing: 0,
        duration: 300,
        essential: true,
      })
    }

    setIsSatelliteView(false)
    setIsTilted(false)
    setSecondaryControlView('map')
  }, [isTilted, mapboxMap])
  const handleShowSatellite = useCallback(() => {
    setIsSatelliteView(true)
    setSecondaryControlView('satellite')
  }, [])
  const handleToggleTilt = useCallback(() => {
    if (!mapboxMap || !isSatelliteView) {
      return
    }

    setIsTilted((previousValue) => {
      const nextValue = !previousValue

      mapboxMap.easeTo({
        pitch: nextValue ? TILTED_MAP_PITCH : 0,
        duration: 300,
        essential: true,
      })

      return nextValue
    })
  }, [isSatelliteView, mapboxMap])
  const handleRotateMap = useCallback(() => {
    if (!mapboxMap || !isSatelliteView) {
      return
    }

    mapboxMap.easeTo({
      bearing: mapboxMap.getBearing() - 90,
      duration: 300,
      essential: true,
    })
  }, [isSatelliteView, mapboxMap])
  const handleToggleTerrain = useCallback(() => {
    setShowTerrain((previousValue) => {
      const nextValue = !previousValue

      if (mapboxMap && !isSatelliteView) {
        applyTerrainVisibility(mapboxMap, nextValue)
      }

      return nextValue
    })
  }, [isSatelliteView, mapboxMap])
  const handleToggleSatelliteLabels = useCallback(() => {
    setShowSatelliteLabels((previousValue) => {
      const nextValue = !previousValue

      if (mapboxMap && isSatelliteView) {
        applySatelliteLabelsVisibility(mapboxMap, nextValue)
      }

      return nextValue
    })
  }, [isSatelliteView, mapboxMap])
  const handleOpenMapSecondaryControls = useCallback(() => {
    if (!isSatelliteView) {
      setSecondaryControlView('map')
    }
  }, [isSatelliteView])
  const handleOpenSatelliteSecondaryControls = useCallback(() => {
    if (isSatelliteView) {
      setSecondaryControlView('satellite')
    }
  }, [isSatelliteView])
  const handleCloseSecondaryControls = useCallback(() => {
    setSecondaryControlView(undefined)
  }, [])

  const handleClusterSelect = useCallback((theme: 'white' | 'black', clusterId: number, coordinate: LngLat) => {
    setActiveMarkerInfoWindowOpen(false)

    if (theme === 'white') {
      handleWhiteClusterClick(clusterId, coordinate)
      return
    }

    handleBlackClusterClick(clusterId, coordinate)
  }, [handleBlackClusterClick, handleWhiteClusterClick])
  const handleWhiteClusterSelect = useCallback((clusterId: number, coordinate: LngLat) => {
    handleClusterSelect('white', clusterId, coordinate)
  }, [handleClusterSelect])
  const handleBlackClusterSelect = useCallback((clusterId: number, coordinate: LngLat) => {
    handleClusterSelect('black', clusterId, coordinate)
  }, [handleClusterSelect])

  useEffect(() => {
    if (!mapHandle || !mapboxMap) {
      return
    }

    const previousFitState = fitMarkersGuardRef.current
    if (
      previousFitState?.mapHandle === mapHandle &&
      previousFitState?.mapboxMap === mapboxMap &&
      previousFitState?.markersFitKey === markersFitKey &&
      previousFitState?.focusOnFirstMarker === focusOnFirstMarker
    ) {
      return
    }

    fitMarkersGuardRef.current = {
      mapHandle,
      mapboxMap,
      markersFitKey,
      focusOnFirstMarker,
    }

    fitMarkersToMap(mapHandle, markers, focusOnFirstMarker)

    if (focusOnFirstMarker) {
      return
    }

    if (markers.length !== 2) {
      return
    }

    const bounds = getBoundsFromMarkers(markers)
    if (!bounds) {
      return
    }

    const longitudeSpan = getLongitudeSpan(bounds.east, bounds.west)
    const latitudeSpan = bounds.north - bounds.south
    const boundsCenter = getMapBoundsCenter(bounds)

    if (
      longitudeSpan > PROPERTY_MAP_TWO_MARKER_MAX_LONGITUDE_SPAN ||
      latitudeSpan > PROPERTY_MAP_TWO_MARKER_MAX_LATITUDE_SPAN
    ) {
      return
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const currentZoom = mapboxMap.getZoom()
      const targetZoom = Math.min(
        Math.max(currentZoom, PROPERTY_MAP_TWO_MARKER_MIN_ZOOM),
        mapboxMap.getMaxZoom(),
      )

      if (Math.abs(targetZoom - currentZoom) < 0.01) {
        return
      }

      mapboxMap.easeTo({
        center: [boundsCenter.lng, boundsCenter.lat],
        zoom: targetZoom,
        duration: 0,
        essential: true,
      })
    })

    return () => {
      window.cancelAnimationFrame(animationFrameId)
    }
  }, [focusOnFirstMarker, mapHandle, mapboxMap, markers, markersFitKey])

  const applyMapPresentation = useCallback((map: MapboxMapInstance) => {
    if (!map.isStyleLoaded()) {
      return
    }

    if (isSatelliteView) {
      applyTerrainVisibility(map, false)
      applySatelliteLabelsVisibility(map, showSatelliteLabels)
    } else {
      applySatelliteLabelsVisibility(map, true)
      applyTerrainVisibility(map, showTerrain)
    }

    const targetPitch = isSatelliteView && isTilted ? TILTED_MAP_PITCH : 0
    if (Math.abs(map.getPitch() - targetPitch) > PITCH_SYNC_EPSILON) {
      map.setPitch(targetPitch)
    }
  }, [isSatelliteView, isTilted, showSatelliteLabels, showTerrain])

  useEffect(() => {
    if (!mapboxMap) {
      return
    }

    const handleStyleLoad = () => {
      applyMapPresentation(mapboxMap)
    }

    mapboxMap.on('style.load', handleStyleLoad)
    applyMapPresentation(mapboxMap)

    return () => {
      mapboxMap.off('style.load', handleStyleLoad)
    }
  }, [applyMapPresentation, mapboxMap])

  useEffect(() => {
    if (activeMarker && !markers.some(marker => marker.id === activeMarker.id)) {
      setActiveMarker(undefined)
      setActiveMarkerInfoWindowOpen(false)
    }
  }, [activeMarker, markers, markersKey])

  const classes = clsx(
    'PropertyMap',
    className,
  )

  const renderClusterLayer = useCallback((
    theme: 'white' | 'black',
    clusterMarkers: Array<ClusterableMarker>,
    clusters: Array<PropertyMapClusterFeature>,
    onClusterSelect: (clusterId: number, coordinate: LngLat) => void,
  ) => {
    return clusters.map((feature) => {
      if (isMapClusterFeature(feature)) {
        const [lng, lat] = feature.geometry.coordinates
        return (
          <PropertyMapClusterMarker
            key={`${theme}-${feature.id}`}
            theme={theme}
            count={feature.properties.point_count_abbreviated ?? feature.properties.point_count}
            lat={lat}
            lng={lng}
            clusterId={Number(feature.id)}
            onSelect={onClusterSelect}
          />
        )
      }

      const marker = clusterMarkers[feature.properties.originalIndex]
      const isActive = (showActiveMarkerState && activeMarker?.id === marker?.id) || highlightedMarkerId === marker?.id

      if (!marker) {
        return null
      }

      return (
        <PropertyMapMarker
          key={marker.id}
          marker={marker}
          active={Boolean(isActive)}
          onSelect={handleMarkerSelect}
        />
      )
    })
  }, [activeMarker?.id, handleMarkerSelect, highlightedMarkerId, showActiveMarkerState])

  return (
    <MapRoot className={classes} style={wrapperStyle}>
      <MapPopupStyles />
      <MapboxProvider>
        <MapboxMap
          id="le-map"
          initialViewport={initialViewport}
          mapStyle={mapStyle}
          containerStyle={{
            height,
            width: '100%',
            flexGrow: 1,
          }}
          loadingElement={<LoadingIndicator inline />}
          onLoad={handleMapLoad}
          onMapReady={handleMapReady}
          onMove={handleMapMove}
          onMoveEnd={handleMapMoveEnd}
          onClick={handleMapClick}
          {...mapboxMapProps}
        >
          {showMapTypeControl && (
            <MapTypeControlsContainer onMouseLeave={handleCloseSecondaryControls}>
              <TopLeftControls>
                <PrimaryControlButtonWrapper>
                  <MapTypeButton
                    className={clsx({ isActive: !isSatelliteView })}
                    type="button"
                    aria-pressed={!isSatelliteView}
                    onClick={handleShowRoadmap}
                    onMouseEnter={handleOpenMapSecondaryControls}
                    onFocus={handleOpenMapSecondaryControls}
                  >
                    {intl.formatMessage({
                      id: 'sC4MLjYL3YiGbAYKsvV-',
                      defaultMessage: 'Map',
                      description: 'Map type switcher option for standard map view on property map',
                    })}
                  </MapTypeButton>
                  {secondaryControlView === 'map' && !isSatelliteView && (
                    <SecondaryTopLeftControls>
                      <OptionCheckboxLabel>
                        <OptionCheckbox
                          type="checkbox"
                          checked={showTerrain}
                          onChange={handleToggleTerrain}
                        />
                        {intl.formatMessage({
                          id: 'Dcsi3MVL3h6ReGZA8eLt',
                          defaultMessage: 'Terrain',
                          description: 'Terrain layer toggle label for property map standard map view',
                        })}
                      </OptionCheckboxLabel>
                    </SecondaryTopLeftControls>
                  )}
                </PrimaryControlButtonWrapper>
                <PrimaryControlButtonWrapper>
                  <MapTypeButton
                    className={clsx({ isActive: isSatelliteView })}
                    type="button"
                    aria-pressed={isSatelliteView}
                    onClick={handleShowSatellite}
                    onMouseEnter={handleOpenSatelliteSecondaryControls}
                    onFocus={handleOpenSatelliteSecondaryControls}
                  >
                    {intl.formatMessage({
                      id: 'MTwpQviLjrIHezazwXu_',
                      defaultMessage: 'Satellite',
                      description: 'Map type switcher option for satellite map view on property map',
                    })}
                  </MapTypeButton>
                  {secondaryControlView === 'satellite' && isSatelliteView && (
                    <SecondaryTopLeftControls className={clsx('alignRight')}>
                      <OptionCheckboxLabel>
                        <OptionCheckbox
                          type="checkbox"
                          checked={showSatelliteLabels}
                          onChange={handleToggleSatelliteLabels}
                        />
                        {intl.formatMessage({
                          id: 'RqUKiua5pwTFb-lhtrg2',
                          defaultMessage: 'Labels',
                          description: 'Labels layer toggle label for property map satellite map view',
                        })}
                      </OptionCheckboxLabel>
                    </SecondaryTopLeftControls>
                  )}
                </PrimaryControlButtonWrapper>
              </TopLeftControls>
            </MapTypeControlsContainer>
          )}
          {isSatelliteView && (
            <RightControlsContainer className={clsx({ withFullscreenControl: showFullscreenControl })}>
              <TiltButton
                type="button"
                aria-label={intl.formatMessage({
                  id: '7sIfLbE-JdD49zdNprIl',
                  defaultMessage: 'Rotate map counterclockwise',
                  description: 'Accessibility label for the rotate map counterclockwise button on the property map',
                })}
                title={intl.formatMessage({
                  id: 'seWy-DezooJB1_4jaUUR',
                  defaultMessage: 'Rotate map counterclockwise',
                  description: 'Tooltip for the rotate map counterclockwise button on the property map',
                })}
                onClick={handleRotateMap}
              >
                <RotateButtonIcon />
              </TiltButton>
              <TiltButton
                className={clsx({ isActive: isTilted })}
                type="button"
                aria-pressed={isTilted}
                aria-label={intl.formatMessage({
                  id: 'Ju2DKS-ZfFGS7l8ZTRFJ',
                  defaultMessage: 'Tilt map',
                  description: 'Accessibility label for the tilt map button on the property map',
                })}
                title={intl.formatMessage({
                  id: 'zvoGhQ2gRhpHnXvQ8ffu',
                  defaultMessage: 'Tilt map',
                  description: 'Tooltip for the tilt map button on the property map',
                })}
                onClick={handleToggleTilt}
              >
                <TiltButtonIcon className={clsx({ isTilted })} />
              </TiltButton>
            </RightControlsContainer>
          )}
          {showFullscreenControl && <FullscreenControl position="top-right" />}
          {renderClusterLayer('white', whiteClusterMarkers, whiteClusters, handleWhiteClusterSelect)}
          {renderClusterLayer('black', blackClusterMarkers, blackClusters, handleBlackClusterSelect)}
          {activeMarker && renderInfoWindow && activeMarkerInfoWindowOpen && (
            <MapboxPopup
              anchor="bottom"
              className="property-map-popup"
              closeButton={false}
              closeOnClick={false}
              closeOnMove={false}
              coordinate={{ lat: activeMarker.latitude, lng: activeMarker.longitude }}
              maxWidth="570px"
              offset={55}
              onClose={() => setActiveMarkerInfoWindowOpen(false)}
            >
              {renderInfoWindow(activeMarker, toggleActiveMarkerInfoWindow)}
            </MapboxPopup>
          )}
        </MapboxMap>
      </MapboxProvider>
    </MapRoot>
  )
})

export default PropertyMap
